import React, { useState, useMemo } from "react";
import {
  MdSearch, MdAdd, MdClose, MdArrowBack, MdEdit, MdBlock,
  MdCheckCircle, MdWarning, MdSecurity, MdVpnKey, MdDelete,
  MdInfo, MdPerson, MdHistory,
} from "react-icons/md";
import {
  FiUsers, FiShield, FiMap, FiBarChart2, FiSettings,
  FiUser, FiMail, FiPhone, FiEye, FiCheck, FiX,
} from "react-icons/fi";
import { siteDummyData } from "../../Data/SiteData";
import { useRole } from "../../context/RoleContext";
import { canInviteUser, canChangeRole, canSuspendUser, canAssignSiteToUser, canResetPassword } from "../../utils/permissions";
import "./UserManagement.css";

// ─── Static data ──────────────────────────────────────────────────────────────

const ROLES = [
  { value: "Org Admin",    label: "Org Admin",    level: "org",  color: "#7c3aed", desc: "Full access to all org sites and users" },
  { value: "Org Manager",  label: "Org Manager",  level: "org",  color: "#0ea5e9", desc: "Manage sites, view reports across org" },
  { value: "Site Admin",   label: "Site Admin",   level: "site", color: "#16a34a", desc: "Full control within assigned sites" },
  { value: "Operator",     label: "Operator",     level: "site", color: "#f59e0b", desc: "Control devices and acknowledge alerts" },
  { value: "Technician",   label: "Technician",   level: "site", color: "#f97316", desc: "Configure and maintain devices" },
  { value: "Viewer",       label: "Viewer",       level: "site", color: "#64748b", desc: "Read-only dashboards and reports" },
];

const STATUS_META = {
  Active:    { color: "#16a34a", bg: "#dcfce7", icon: <MdCheckCircle size={13} /> },
  Pending:   { color: "#d97706", bg: "#fef3c7", icon: <MdWarning size={13} /> },
  Suspended: { color: "#dc2626", bg: "#fee2e2", icon: <MdBlock size={13} /> },
};

// Permission matrix per role: module → [actions allowed]
const PERM_MATRIX = {
  "Org Admin":   { Dashboard: ["View"], Devices: ["View","Control","Configure","Delete"], Alerts: ["View","Acknowledge","Resolve"], Reports: ["View","Export"], Settings: ["View","Edit","Delete"], Users: ["View","Create","Edit","Delete"] },
  "Org Manager": { Dashboard: ["View"], Devices: ["View","Control"], Alerts: ["View","Acknowledge","Resolve"], Reports: ["View","Export"], Settings: ["View"], Users: ["View","Create","Edit"] },
  "Site Admin":  { Dashboard: ["View"], Devices: ["View","Control","Configure"], Alerts: ["View","Acknowledge","Resolve"], Reports: ["View","Export"], Settings: ["View","Edit"], Users: ["View","Edit"] },
  "Operator":    { Dashboard: ["View"], Devices: ["View","Control"], Alerts: ["View","Acknowledge"], Reports: ["View"], Settings: [], Users: ["View"] },
  "Technician":  { Dashboard: ["View"], Devices: ["View","Configure"], Alerts: ["View","Acknowledge"], Reports: ["View"], Settings: ["View"], Users: [] },
  "Viewer":      { Dashboard: ["View"], Devices: ["View"], Alerts: ["View"], Reports: ["View"], Settings: [], Users: [] },
};
const ALL_MODULES  = ["Dashboard", "Devices", "Alerts", "Reports", "Settings", "Users"];
const ALL_ACTIONS  = ["View", "Control", "Configure", "Delete", "Acknowledge", "Resolve", "Export", "Create", "Edit"];

// Permission preview bullets per role
const PERM_BULLETS = {
  "Org Admin":   { can: ["View all sites and devices", "Control any device", "Manage all users", "Edit org settings", "Export reports"], cannot: ["Modify platform-level settings"] },
  "Org Manager": { can: ["View all sites", "Control devices", "Create and edit users", "Export reports"], cannot: ["Delete users", "Delete devices", "Edit billing"] },
  "Site Admin":  { can: ["Manage assigned site devices", "Edit site settings", "Acknowledge and resolve alerts", "Export site reports"], cannot: ["Access other sites", "Manage org-level users"] },
  "Operator":    { can: ["View dashboards", "Control devices", "Acknowledge alerts"], cannot: ["Configure devices", "Delete anything", "Manage users"] },
  "Technician":  { can: ["View dashboards", "Configure and maintain devices", "Acknowledge alerts"], cannot: ["Control devices remotely", "Delete anything", "Manage users"] },
  "Viewer":      { can: ["View dashboards and reports"], cannot: ["Control or configure devices", "Acknowledge alerts", "Manage users", "Export data"] },
};

// Dummy users — pulled from siteDummyData + extras
const DUMMY_USERS = [
  {
    id: "SU-001", name: "IoTMATE SuperAdmin", email: "admin@iotmate.io",
    phone: "+91-20-1234-5678", department: "Platform", orgId: null,
    globalRole: "Super Admin", status: "Active", mfaEnabled: true,
    createdAt: "2022-01-01", lastLogin: "Today 08:00",
    siteAccess: [],
    activity: [
      { action: "Login", detail: "Successful login from 192.168.1.1", time: "Today 08:00" },
      { action: "Create Org", detail: "Created ORG-008: Shri Renuka Textiles", time: "Yesterday 15:30" },
    ],
  },
  {
    id: "U-001", name: "Arjun Tata", email: "arjun.tata@tataindustries.com",
    phone: "+91-22-6665-8282", department: "Management", orgId: "ORG-001",
    globalRole: "Org Admin", status: "Active", mfaEnabled: true,
    createdAt: "2023-01-15", lastLogin: "Today 09:30",
    siteAccess: [{ siteId: "SITE-001", siteName: "Mumbai Industrial Plant", role: "Org Admin" }],
    activity: [
      { action: "Login", detail: "Successful login", time: "Today 09:30" },
      { action: "Edit Site", detail: "Updated SITE-001 notification settings", time: "Yesterday 11:00" },
    ],
  },
  {
    id: "U-002", name: "Rajesh Mehta", email: "rajesh.mehta@tataindustries.com",
    phone: "+91-98200-12345", department: "Operations", orgId: "ORG-001",
    globalRole: "Site Admin", status: "Active", mfaEnabled: false,
    createdAt: "2023-04-15", lastLogin: "Today 09:12",
    siteAccess: [{ siteId: "SITE-001", siteName: "Mumbai Industrial Plant", role: "Site Admin" }],
    activity: [
      { action: "Login", detail: "Successful login", time: "Today 09:12" },
      { action: "Resolve Alert", detail: "Resolved SA-003 (PM2.5 threshold)", time: "Today 09:20" },
    ],
  },
  {
    id: "U-003", name: "Kavita Sharma", email: "kavita.sharma@tataindustries.com",
    phone: "+91-98200-23456", department: "Facility", orgId: "ORG-001",
    globalRole: "Operator", status: "Active", mfaEnabled: false,
    createdAt: "2023-04-15", lastLogin: "Today 10:45",
    siteAccess: [{ siteId: "SITE-001", siteName: "Mumbai Industrial Plant", role: "Operator" }],
    activity: [
      { action: "Login", detail: "Successful login", time: "Today 10:45" },
      { action: "Control Device", detail: "Activated Pump Controller 01", time: "Today 10:50" },
    ],
  },
  {
    id: "U-004", name: "Sunil Patil", email: "sunil.patil@tataindustries.com",
    phone: "+91-98200-34567", department: "Technical", orgId: "ORG-001",
    globalRole: "Technician", status: "Active", mfaEnabled: false,
    createdAt: "2023-04-15", lastLogin: "Yesterday",
    siteAccess: [{ siteId: "SITE-001", siteName: "Mumbai Industrial Plant", role: "Technician" }],
    activity: [{ action: "Login", detail: "Successful login", time: "Yesterday 08:30" }],
  },
  {
    id: "U-005", name: "Priya Kulkarni", email: "priya.kulkarni@infosys.com",
    phone: "+91-97300-45678", department: "IT", orgId: "ORG-002",
    globalRole: "Org Admin", status: "Active", mfaEnabled: true,
    createdAt: "2023-06-01", lastLogin: "Today 08:30",
    siteAccess: [{ siteId: "SITE-002", siteName: "Pune Office Tower", role: "Org Admin" }],
    activity: [{ action: "Login", detail: "Successful login", time: "Today 08:30" }],
  },
  {
    id: "U-006", name: "Sanjay More", email: "sanjay.more@infosys.com",
    phone: "+91-97300-56789", department: "Facility", orgId: "ORG-002",
    globalRole: "Operator", status: "Active", mfaEnabled: false,
    createdAt: "2023-07-20", lastLogin: "Today 09:00",
    siteAccess: [{ siteId: "SITE-002", siteName: "Pune Office Tower", role: "Operator" }],
    activity: [{ action: "Login", detail: "Successful login", time: "Today 09:00" }],
  },
  {
    id: "U-007", name: "Anil Gaikwad", email: "anil.gaikwad@sula.com",
    phone: "+91-94220-78901", department: "Agriculture", orgId: "ORG-003",
    globalRole: "Org Admin", status: "Active", mfaEnabled: false,
    createdAt: "2023-12-20", lastLogin: "Today 06:15",
    siteAccess: [{ siteId: "SITE-003", siteName: "Nashik Vineyard & Farm", role: "Site Admin" }],
    activity: [{ action: "Login", detail: "Successful login", time: "Today 06:15" }],
  },
  {
    id: "U-008", name: "Vikram Singh", email: "vikram.singh@bajaj.com",
    phone: "+91-96570-23456", department: "Operations", orgId: "ORG-004",
    globalRole: "Org Admin", status: "Active", mfaEnabled: true,
    createdAt: "2022-11-05", lastLogin: "Today 07:30",
    siteAccess: [{ siteId: "SITE-004", siteName: "Aurangabad Auto Parts Factory", role: "Org Admin" }],
    activity: [
      { action: "Login", detail: "Successful login", time: "Today 07:30" },
      { action: "Acknowledge Alert", detail: "Acknowledged SA-031 (STP compliance)", time: "Yesterday 16:00" },
    ],
  },
  {
    id: "U-009", name: "Deepak Verma", email: "deepak.verma@tcs.com",
    phone: "+91-91220-56789", department: "IT Operations", orgId: "ORG-006",
    globalRole: "Org Admin", status: "Active", mfaEnabled: true,
    createdAt: "2022-08-01", lastLogin: "Today 08:00",
    siteAccess: [{ siteId: "SITE-006", siteName: "Navi Mumbai IT Campus", role: "Org Admin" }],
    activity: [{ action: "Login", detail: "Successful login", time: "Today 08:00" }],
  },
  {
    id: "U-010", name: "Ganesh Patil", email: "ganesh.patil@bhogavati.com",
    phone: "+91-231-265-8901", department: "Mill Operations", orgId: "ORG-007",
    globalRole: "Org Admin", status: "Active", mfaEnabled: false,
    createdAt: "2024-11-15", lastLogin: "Today 07:00",
    siteAccess: [{ siteId: "SITE-007", siteName: "Kolhapur Sugar Mill", role: "Site Admin" }],
    activity: [{ action: "Login", detail: "Successful login", time: "Today 07:00" }],
  },
  {
    id: "U-011", name: "Ramesh Jadhav", email: "ramesh.jadhav@renuka.com",
    phone: "+91-98901-89012", department: "Factory", orgId: "ORG-008",
    globalRole: "Site Admin", status: "Suspended", mfaEnabled: false,
    createdAt: "2023-05-20", lastLogin: "14 days ago",
    siteAccess: [{ siteId: "SITE-008", siteName: "Solapur Textile Factory", role: "Site Admin" }],
    activity: [{ action: "Login Failed", detail: "3 consecutive failed login attempts", time: "14 days ago" }],
  },
  {
    id: "U-012", name: "Neha Shah", email: "neha.shah@lodha.com",
    phone: "+91-89760-34567", department: "Management", orgId: "ORG-005",
    globalRole: "Org Admin", status: "Active", mfaEnabled: true,
    createdAt: "2023-09-10", lastLogin: "Today 09:00",
    siteAccess: [{ siteId: "SITE-005", siteName: "Thane Residential Complex", role: "Org Admin" }],
    activity: [{ action: "Login", detail: "Successful login", time: "Today 09:00" }],
  },
  {
    id: "U-013", name: "Smita Pandey", email: "smita.pandey@tcs.com",
    phone: "+91-91220-67890", department: "Facility", orgId: "ORG-006",
    globalRole: "Operator", status: "Pending", mfaEnabled: false,
    createdAt: "2025-02-20", lastLogin: "Never",
    siteAccess: [{ siteId: "SITE-006", siteName: "Navi Mumbai IT Campus", role: "Operator" }],
    activity: [{ action: "Invite Sent", detail: "Invitation email sent to smita.pandey@tcs.com", time: "2025-02-20" }],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function userAvatar(user) {
  const letter = user.name.charAt(0).toUpperCase();
  const colors = ["#7c3aed","#0ea5e9","#16a34a","#f59e0b","#ef4444","#06b6d4","#d946ef","#f97316","#10b981","#3b82f6","#8b5cf6","#ec4899","#14b8a6"];
  const idx = parseInt(user.id.replace(/\D/g, ""), 10) || 0;
  return { letter, color: colors[idx % colors.length] };
}

function roleMeta(role) {
  return ROLES.find(r => r.value === role) || { color: "#64748b", label: role };
}

function orgName(orgId) {
  const o = siteDummyData.organizations.find(o => o.id === orgId);
  return o ? o.name : "Platform";
}

// ─── Small Badges ─────────────────────────────────────────────────────────────

function RoleBadge({ role }) {
  const m = roleMeta(role);
  return (
    <span className="um-badge" style={{ background: m.color + "20", color: m.color }}>
      {role}
    </span>
  );
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.Suspended;
  return (
    <span className="um-badge um-badge-icon" style={{ background: m.bg, color: m.color }}>
      {m.icon} {status}
    </span>
  );
}

// ─── Invite User Modal ────────────────────────────────────────────────────────

const EMPTY_INVITE = {
  name: "", email: "", phone: "", department: "",
  orgId: siteDummyData.organizations[0]?.id || "",
  role: "Viewer", selectedSites: [],
};

function InviteModal({ onClose, onSave }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...EMPTY_INVITE });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const sites = useMemo(
    () => siteDummyData.sites.filter(s => s.orgId === form.orgId),
    [form.orgId]
  );
  const selectedRole = ROLES.find(r => r.value === form.role) || ROLES[ROLES.length - 1];
  const isOrgLevel  = selectedRole.level === "org";
  const bullets     = PERM_BULLETS[form.role] || PERM_BULLETS["Viewer"];

  const toggleSite = siteId => {
    set("selectedSites", form.selectedSites.includes(siteId)
      ? form.selectedSites.filter(x => x !== siteId)
      : [...form.selectedSites, siteId]);
  };

  const steps = ["Basic Info", "Role", "Site Access", "Preview", "Invite"];

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="um-modal-hdr">
          <div className="um-modal-title"><FiUsers size={18} /> Invite New User</div>
          <button className="um-modal-close" onClick={onClose}><MdClose size={18} /></button>
        </div>

        {/* Step nav */}
        <div className="um-step-nav">
          {steps.map((s, i) => (
            <div key={i} className={`um-step ${step === i ? "um-step-active" : ""} ${step > i ? "um-step-done" : ""}`} onClick={() => i < step && setStep(i)}>
              <div className="um-step-circle">{step > i ? <FiCheck size={11} /> : i + 1}</div>
              <span>{s}</span>
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="um-modal-body">

          {/* Step 0: Basic Info */}
          {step === 0 && (
            <div className="um-form-grid">
              <div className="um-form-group um-span-2">
                <label>Full Name *</label>
                <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="John Smith" />
              </div>
              <div className="um-form-group">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="john@company.com" />
              </div>
              <div className="um-form-group">
                <label>Phone</label>
                <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+91-XXXXX-XXXXX" />
              </div>
              <div className="um-form-group">
                <label>Department</label>
                <input value={form.department} onChange={e => set("department", e.target.value)} placeholder="e.g. Operations" />
              </div>
              <div className="um-form-group">
                <label>Organization *</label>
                <select value={form.orgId} onChange={e => { set("orgId", e.target.value); set("selectedSites", []); }}>
                  {siteDummyData.organizations.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 1: Role */}
          {step === 1 && (
            <div className="um-role-grid">
              {ROLES.map(r => (
                <div
                  key={r.value}
                  className={`um-role-card ${form.role === r.value ? "um-role-card-active" : ""}`}
                  style={form.role === r.value ? { borderColor: r.color, boxShadow: `0 0 0 3px ${r.color}22` } : {}}
                  onClick={() => set("role", r.value)}
                >
                  <div className="um-role-card-name" style={{ color: r.color }}>{r.label}</div>
                  <div className="um-role-card-level">{r.level === "org" ? "Organization Level" : "Site Level"}</div>
                  <div className="um-role-card-desc">{r.desc}</div>
                </div>
              ))}
            </div>
          )}

          {/* Step 2: Site Access */}
          {step === 2 && (
            <div>
              {isOrgLevel ? (
                <div className="um-info-box">
                  <MdInfo size={15} />
                  <span><strong>{form.role}</strong> is an organization-level role. This user will automatically have access to all sites in <strong>{orgName(form.orgId)}</strong>.</span>
                </div>
              ) : (
                <>
                  <div className="um-info-box">
                    <MdInfo size={15} />
                    <span>Select one or more sites this user can access with the <strong>{form.role}</strong> role.</span>
                  </div>
                  <div className="um-site-list">
                    {sites.map(site => {
                      const sel = form.selectedSites.includes(site.id);
                      return (
                        <div
                          key={site.id}
                          className={`um-site-item ${sel ? "um-site-item-sel" : ""}`}
                          onClick={() => toggleSite(site.id)}
                        >
                          <div className={`um-site-checkbox ${sel ? "um-site-checkbox-sel" : ""}`}>
                            {sel && <FiCheck size={12} />}
                          </div>
                          <div>
                            <div className="um-site-name">{site.name}</div>
                            <div className="um-site-id">{site.id} · {site.type}</div>
                          </div>
                          <StatusBadge status={site.status} />
                        </div>
                      );
                    })}
                    {sites.length === 0 && (
                      <div className="um-empty-msg">No sites found for this organization.</div>
                    )}
                  </div>
                  <div className="um-site-count">
                    {form.selectedSites.length} of {sites.length} sites selected
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: Permission Preview */}
          {step === 3 && (
            <div>
              <div className="um-preview-role-pill" style={{ background: selectedRole.color + "18", color: selectedRole.color }}>
                <FiShield size={14} /> {form.role} — {selectedRole.desc}
              </div>
              <div className="um-preview-cols">
                <div className="um-preview-col um-preview-can">
                  <div className="um-preview-col-title"><FiCheck size={13} /> This user CAN</div>
                  {bullets.can.map((b, i) => (
                    <div key={i} className="um-preview-bullet um-preview-can-item">✔ {b}</div>
                  ))}
                </div>
                <div className="um-preview-col um-preview-cannot">
                  <div className="um-preview-col-title"><FiX size={13} /> This user CANNOT</div>
                  {bullets.cannot.map((b, i) => (
                    <div key={i} className="um-preview-bullet um-preview-cannot-item">✘ {b}</div>
                  ))}
                </div>
              </div>

              {/* Permission matrix mini */}
              <div className="um-perm-matrix-wrap">
                <div className="um-perm-matrix-title">Permission Matrix</div>
                <div className="um-perm-matrix">
                  <div className="um-perm-hdr">
                    <div />
                    {ALL_ACTIONS.slice(0, 6).map(a => <div key={a} className="um-perm-action">{a}</div>)}
                  </div>
                  {ALL_MODULES.map(mod => {
                    const allowed = PERM_MATRIX[form.role]?.[mod] || [];
                    return (
                      <div className="um-perm-row" key={mod}>
                        <div className="um-perm-mod">{mod}</div>
                        {ALL_ACTIONS.slice(0, 6).map(a => (
                          <div key={a} className="um-perm-cell">
                            {allowed.includes(a)
                              ? <span className="um-perm-yes"><FiCheck size={11} /></span>
                              : <span className="um-perm-no"><FiX size={11} /></span>
                            }
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Send Invite */}
          {step === 4 && (
            <div className="um-invite-confirm">
              <div className="um-invite-icon">✉️</div>
              <h3>Ready to send invite</h3>
              <div className="um-invite-summary">
                <div className="um-inv-row"><span>Name</span><strong>{form.name}</strong></div>
                <div className="um-inv-row"><span>Email</span><strong>{form.email}</strong></div>
                <div className="um-inv-row"><span>Organization</span><strong>{orgName(form.orgId)}</strong></div>
                <div className="um-inv-row"><span>Role</span><strong><RoleBadge role={form.role} /></strong></div>
                <div className="um-inv-row">
                  <span>Sites</span>
                  <strong>{isOrgLevel ? "All sites (org-level)" : form.selectedSites.length > 0 ? `${form.selectedSites.length} site(s)` : "None"}</strong>
                </div>
              </div>
              <div className="um-info-box" style={{ marginTop: 16 }}>
                <MdInfo size={15} />
                <span>An invitation email will be sent. The user must set their password on first login.</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="um-modal-footer">
          <button className="um-btn um-btn-ghost" onClick={onClose}>Cancel</button>
          <div className="um-modal-nav">
            {step > 0 && <button className="um-btn um-btn-outline" onClick={() => setStep(s => s - 1)}>← Back</button>}
            {step < steps.length - 1 && (
              <button className="um-btn um-btn-primary" onClick={() => setStep(s => s + 1)}>Next →</button>
            )}
            {step === steps.length - 1 && (
              <button className="um-btn um-btn-primary" onClick={() => onSave(form)}>Send Invitation</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── User Detail: Overview Tab ────────────────────────────────────────────────

function OverviewTab({ user }) {
  const av = userAvatar(user);
  const rm = roleMeta(user.globalRole);
  return (
    <div className="um-detail-ovw">
      <div className="um-detail-hero">
        <div className="um-detail-avatar" style={{ background: av.color }}>{av.letter}</div>
        <div className="um-detail-hero-info">
          <h2>{user.name}</h2>
          <p>{user.email}</p>
          <div className="um-detail-hero-badges">
            <RoleBadge role={user.globalRole} />
            <StatusBadge status={user.status} />
            {user.mfaEnabled && <span className="um-mfa-chip"><MdSecurity size={11} /> MFA On</span>}
          </div>
        </div>
      </div>
      <div className="um-detail-info-grid">
        <div className="um-detail-card">
          <div className="um-detail-card-title">Account Info</div>
          <div className="um-detail-row"><span>Full Name</span><strong>{user.name}</strong></div>
          <div className="um-detail-row"><span>Email</span><strong>{user.email}</strong></div>
          <div className="um-detail-row"><span>Phone</span><strong>{user.phone || "—"}</strong></div>
          <div className="um-detail-row"><span>Department</span><strong>{user.department || "—"}</strong></div>
          <div className="um-detail-row"><span>Last Login</span><strong>{user.lastLogin}</strong></div>
          <div className="um-detail-row"><span>Created</span><strong>{user.createdAt}</strong></div>
        </div>
        <div className="um-detail-card">
          <div className="um-detail-card-title">Access Summary</div>
          <div className="um-detail-row"><span>Role</span><strong style={{ color: rm.color }}>{user.globalRole}</strong></div>
          <div className="um-detail-row"><span>Organization</span><strong>{orgName(user.orgId)}</strong></div>
          <div className="um-detail-row"><span>Sites Access</span><strong>{user.siteAccess.length} site(s)</strong></div>
          <div className="um-detail-row"><span>MFA</span><strong>{user.mfaEnabled ? "✅ Enabled" : "❌ Disabled"}</strong></div>
          <div className="um-detail-row"><span>Status</span><strong><StatusBadge status={user.status} /></strong></div>
        </div>
      </div>
    </div>
  );
}

// ─── User Detail: Site Access Tab ─────────────────────────────────────────────

function SiteAccessTab({ user }) {
  const { currentRole } = useRole();
  return (
    <div className="um-tab-section">
      <div className="um-tab-hdr">
        <span>{user.siteAccess.length} site(s) assigned</span>
        {canAssignSiteToUser(currentRole) && (
          <button className="um-btn um-btn-primary um-btn-sm"><MdAdd size={14} /> Add Site</button>
        )}
      </div>
      {user.siteAccess.length === 0 ? (
        <div className="um-empty-state"><FiMap size={36} /><p>No site access assigned. {user.globalRole === "Org Admin" ? "This user has org-level access to all sites." : "Assign sites using the button above."}</p></div>
      ) : (
        <table className="um-table">
          <thead>
            <tr><th>Site</th><th>Type</th><th>Site Role</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {user.siteAccess.map((sa, i) => {
              const site = siteDummyData.sites.find(s => s.id === sa.siteId);
              return (
                <tr key={i}>
                  <td><strong>{sa.siteName}</strong><br /><span className="um-dim">{sa.siteId}</span></td>
                  <td>{site?.type || "—"}</td>
                  <td><RoleBadge role={sa.role} /></td>
                  <td><StatusBadge status={site?.status || "Active"} /></td>
                  <td>
                    <div className="um-action-btns">
                      <button className="um-icon-btn" title="Edit role"><MdEdit size={13} /></button>
                      <button className="um-icon-btn um-icon-btn-red" title="Remove"><MdDelete size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── User Detail: Activity Log Tab ────────────────────────────────────────────

function ActivityTab({ user }) {
  const actionColor = action => {
    if (action.includes("Login Failed")) return "#dc2626";
    if (action.includes("Login")) return "#16a34a";
    if (action.includes("Delete") || action.includes("Suspend")) return "#ef4444";
    if (action.includes("Resolve") || action.includes("Create")) return "#7c3aed";
    return "#0ea5e9";
  };
  return (
    <div className="um-tab-section">
      <div className="um-activity-timeline">
        {(user.activity || []).map((a, i) => (
          <div className="um-activity-item" key={i}>
            <div className="um-activity-dot" style={{ background: actionColor(a.action) }} />
            <div className="um-activity-body">
              <div className="um-activity-action" style={{ color: actionColor(a.action) }}>{a.action}</div>
              <div className="um-activity-detail">{a.detail}</div>
              <div className="um-activity-time">{a.time}</div>
            </div>
          </div>
        ))}
        {(!user.activity || user.activity.length === 0) && (
          <div className="um-empty-state"><MdHistory size={36} /><p>No activity recorded yet.</p></div>
        )}
      </div>
    </div>
  );
}

// ─── User Detail: Permissions Tab ────────────────────────────────────────────

function PermissionsTab({ user }) {
  const allowed = PERM_MATRIX[user.globalRole] || {};
  return (
    <div className="um-tab-section">
      <div className="um-info-box" style={{ marginBottom: 16 }}>
        <MdInfo size={15} />
        <span>Permissions are determined by the role <strong>{user.globalRole}</strong>. Custom roles are coming soon.</span>
      </div>
      <div className="um-perm-matrix-wrap">
        <div className="um-perm-matrix">
          <div className="um-perm-hdr">
            <div />
            {ALL_ACTIONS.map(a => <div key={a} className="um-perm-action">{a}</div>)}
          </div>
          {ALL_MODULES.map(mod => (
            <div className="um-perm-row" key={mod}>
              <div className="um-perm-mod">{mod}</div>
              {ALL_ACTIONS.map(a => (
                <div key={a} className="um-perm-cell">
                  {(allowed[mod] || []).includes(a)
                    ? <span className="um-perm-yes"><FiCheck size={11} /></span>
                    : <span className="um-perm-no"><FiX size={11} /></span>
                  }
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── User Detail: Security Tab ────────────────────────────────────────────────

function SecurityTab({ user }) {
  return (
    <div className="um-tab-section">
      <div className="um-security-grid">
        <div className="um-detail-card">
          <div className="um-detail-card-title">Authentication</div>
          <div className="um-security-item">
            <div>
              <strong>Multi-Factor Authentication</strong>
              <p>Adds an extra layer of security to the account.</p>
            </div>
            <button className={`um-btn um-btn-sm ${user.mfaEnabled ? "um-btn-outline" : "um-btn-primary"}`}>
              {user.mfaEnabled ? "Disable MFA" : "Enable MFA"}
            </button>
          </div>
          <div className="um-security-item" style={{ marginTop: 12 }}>
            <div>
              <strong>Reset Password</strong>
              <p>Send a password reset link to {user.email}.</p>
            </div>
            <button className="um-btn um-btn-outline um-btn-sm"><MdVpnKey size={13} /> Reset</button>
          </div>
        </div>
        <div className="um-detail-card">
          <div className="um-detail-card-title">Sessions</div>
          <div className="um-security-item">
            <div>
              <strong>Terminate All Sessions</strong>
              <p>Log this user out from all devices immediately.</p>
            </div>
            <button className="um-btn um-btn-warning um-btn-sm">Logout All</button>
          </div>
        </div>
      </div>
      <div className="um-danger-zone">
        <div className="um-danger-title">Danger Zone</div>
        <div className="um-danger-row">
          <div><strong>Suspend Account</strong><p>User will be blocked from logging in. Data is preserved.</p></div>
          <button className="um-btn um-btn-warning um-btn-sm">Suspend</button>
        </div>
        <div className="um-danger-row">
          <div><strong>Delete Account</strong><p>Permanently removes the user. This cannot be undone.</p></div>
          <button className="um-btn um-btn-danger um-btn-sm"><MdDelete size={13} /> Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── User Detail View ─────────────────────────────────────────────────────────

const DETAIL_TABS = [
  { id: "overview",     label: "Overview",      icon: <FiUser size={13} /> },
  { id: "sites",        label: "Site Access",   icon: <FiMap size={13} /> },
  { id: "activity",     label: "Activity Logs", icon: <MdHistory size={14} /> },
  { id: "permissions",  label: "Permissions",   icon: <FiShield size={13} /> },
  { id: "security",     label: "Security",      icon: <MdSecurity size={14} /> },
];

function UserDetailView({ user, onBack, onEdit }) {
  const [tab, setTab] = useState("overview");
  const av = userAvatar(user);
  return (
    <div className="um-detail-root">
      <div className="um-detail-topbar">
        <button className="um-back-btn" onClick={onBack}><MdArrowBack size={15} /> Users</button>
        <div className="um-detail-topbar-title">
          <div className="um-mini-avatar" style={{ background: av.color }}>{av.letter}</div>
          <span>{user.name}</span>
          <StatusBadge status={user.status} />
        </div>
        <button className="um-btn um-btn-outline um-btn-sm" onClick={() => onEdit(user)}>
          <MdEdit size={13} /> Edit
        </button>
      </div>
      <div className="um-detail-tab-bar">
        {DETAIL_TABS.map(t => (
          <button key={t.id} className={`um-tab ${tab === t.id ? "um-tab-active" : ""}`} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      <div className="um-detail-panel">
        {tab === "overview"    && <OverviewTab user={user} />}
        {tab === "sites"       && <SiteAccessTab user={user} />}
        {tab === "activity"    && <ActivityTab user={user} />}
        {tab === "permissions" && <PermissionsTab user={user} />}
        {tab === "security"    && <SecurityTab user={user} />}
      </div>
    </div>
  );
}

// ─── User List View ───────────────────────────────────────────────────────────

function UserListView({ users, onSelect, onInvite }) {
  const [search, setSearch]   = useState("");
  const [fRole, setFRole]     = useState("all");
  const [fOrg, setFOrg]       = useState("all");
  const [fStatus, setFStatus] = useState("all");

  const orgs = useMemo(() => siteDummyData.organizations, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(u => {
      if (q && !u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
      if (fRole   !== "all" && u.globalRole !== fRole)    return false;
      if (fOrg    !== "all" && u.orgId !== fOrg)          return false;
      if (fStatus !== "all" && u.status !== fStatus)      return false;
      return true;
    });
  }, [users, search, fRole, fOrg, fStatus]);

  const summary = [
    { label: "Total",     value: users.length,                                      color: "#667eea" },
    { label: "Active",    value: users.filter(u => u.status === "Active").length,    color: "#16a34a" },
    { label: "Pending",   value: users.filter(u => u.status === "Pending").length,   color: "#d97706" },
    { label: "Suspended", value: users.filter(u => u.status === "Suspended").length, color: "#dc2626" },
    { label: "Org Admins",value: users.filter(u => u.globalRole === "Org Admin").length, color: "#7c3aed" },
  ];

  return (
    <div className="um-list-root">
      {/* Header */}
      <div className="um-list-hdr">
        <div>
          <h1 className="um-list-title"><FiUsers size={22} /> User Management</h1>
          <p className="um-list-sub">Manage users, roles, permissions and site access across all organizations.</p>
        </div>
        <button className="um-btn um-btn-primary" onClick={onInvite}>
          <MdAdd size={16} /> Invite User
        </button>
      </div>

      {/* Summary */}
      <div className="um-summary-row">
        {summary.map(p => (
          <div className="um-summary-pill" key={p.label}>
            <span className="um-summary-val" style={{ color: p.color }}>{p.value}</span>
            <span className="um-summary-lbl">{p.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="um-filters">
        <div className="um-search-wrap">
          <MdSearch size={16} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users by name or email…" />
        </div>
        <select value={fRole} onChange={e => setFRole(e.target.value)}>
          <option value="all">All Roles</option>
          {ROLES.map(r => <option key={r.value}>{r.value}</option>)}
        </select>
        <select value={fOrg} onChange={e => setFOrg(e.target.value)}>
          <option value="all">All Organizations</option>
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={fStatus} onChange={e => setFStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          <option>Active</option>
          <option>Pending</option>
          <option>Suspended</option>
        </select>
      </div>

      {/* Table */}
      <div className="um-table-wrap">
        <table className="um-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Organization</th>
              <th>Role</th>
              <th>Sites</th>
              <th>Status</th>
              <th>MFA</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(user => {
              const av = userAvatar(user);
              return (
                <tr key={user.id} className="um-table-row" onClick={() => onSelect(user)}>
                  <td>
                    <div className="um-name-cell">
                      <div className="um-mini-avatar" style={{ background: av.color }}>{av.letter}</div>
                      <div>
                        <div className="um-table-name">{user.name}</div>
                        <div className="um-dim">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="um-dim">{orgName(user.orgId)}</td>
                  <td><RoleBadge role={user.globalRole} /></td>
                  <td>{user.siteAccess.length > 0 ? `${user.siteAccess.length} site(s)` : <span className="um-dim">—</span>}</td>
                  <td><StatusBadge status={user.status} /></td>
                  <td>
                    {user.mfaEnabled
                      ? <span className="um-mfa-chip"><MdSecurity size={11} /> On</span>
                      : <span className="um-dim">Off</span>}
                  </td>
                  <td className="um-dim">{user.lastLogin}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="um-action-btns">
                      <button className="um-icon-btn" title="View" onClick={() => onSelect(user)}><FiEye size={13} /></button>
                      <button className="um-icon-btn" title="Edit"><MdEdit size={13} /></button>
                      <button className="um-icon-btn um-icon-btn-red" title="Suspend"><MdBlock size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="um-empty-row">No users match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function UserManagement() {
  const [users, setUsers]       = useState(DUMMY_USERS);
  const [view, setView]         = useState("list");
  const [selectedUser, selUser] = useState(null);
  const [showInvite, setInvite] = useState(false);

  const handleSave = form => {
    const newUser = {
      id: `U-${Date.now()}`, name: form.name, email: form.email,
      phone: form.phone, department: form.department,
      orgId: form.orgId, globalRole: form.role,
      status: "Pending", mfaEnabled: false,
      createdAt: new Date().toISOString().slice(0, 10),
      lastLogin: "Never",
      siteAccess: form.selectedSites.map(sid => {
        const site = siteDummyData.sites.find(s => s.id === sid);
        return { siteId: sid, siteName: site?.name || sid, role: form.role };
      }),
      activity: [{ action: "Invite Sent", detail: `Invitation sent to ${form.email}`, time: "Just now" }],
    };
    setUsers(prev => [...prev, newUser]);
    setInvite(false);
  };

  return (
    <div className="um-root">
      {view === "list" && (
        <UserListView users={users} onSelect={u => { selUser(u); setView("detail"); }} onInvite={() => setInvite(true)} />
      )}
      {view === "detail" && selectedUser && (
        <UserDetailView
          user={users.find(u => u.id === selectedUser.id) || selectedUser}
          onBack={() => setView("list")}
          onEdit={() => setInvite(true)}
        />
      )}
      {showInvite && <InviteModal onClose={() => setInvite(false)} onSave={handleSave} />}
    </div>
  );
}
